<template>
  <div class="cameraTile" :class="{ 'cameraTile--failed': failed }">
    <video
      ref="video"
      autoplay
      playsinline
      :controls="controls"
      @playing="loading = false"
      @waiting="loading = true"
      @stalled="loading = true"
    ></video>

    <div class="cameraTile__label">{{ label }}</div>

    <div v-if="actions" class="cameraTile__actions">
      <el-button
        circle
        size="mini"
        :icon="muted ? 'el-icon-turn-off-microphone' : 'el-icon-microphone'"
        :title="muted ? $t('vehicleDetail.camera_unmute') : $t('vehicleDetail.camera_mute')"
        @click.stop="$emit('toggle-audio', channel)"
      ></el-button>
      <el-button
        circle
        size="mini"
        :icon="expanded ? 'el-icon-close' : 'el-icon-full-screen'"
        :title="expanded ? $t('vehicleDetail.camera_collapse') : $t('vehicleDetail.camera_expand')"
        @click.stop="$emit('toggle-expand', channel)"
      ></el-button>
      <el-button
        circle
        size="mini"
        icon="fas fa-eye-slash"
        :title="$t('vehicleDetail.camera_hide')"
        @click.stop="$emit('toggle-hide', channel)"
      ></el-button>
    </div>

    <div v-if="loading && !failed" class="cameraTile__overlay">
      <i class="el-icon-loading"></i>
      <span v-if="statusText" class="cameraTile__status">{{ statusText }}</span>
    </div>

    <div v-if="failed" class="cameraTile__overlay cameraTile__overlay--failed">
      <span>{{ statusText || $t('vehicleDetail.camera_unavailable') }}</span>
      <el-button
        size="mini"
        icon="el-icon-refresh"
        @click.stop="retry"
      >{{ $t('vehicleDetail.camera_retry') }}</el-button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { getWhepUrl, getMediaStatus, startStreaming } from '@/utils/cameras'

const MAX_RECONNECT_ATTEMPTS = 10
// How long to wait for the camera to actually start publishing to the
// media server after the videoStart command before giving up.
const STREAM_WAIT_MS = 20000
const STATUS_POLL_MS = 1000
const MONITOR_POLL_MS = 3000
// Once streaming, flag the video as stalled when the media server has had
// no fresh frame from the camera for this long.
const STALL_AFTER_MS = 6000

export default {
  name: 'CameraTile',
  props: {
    device: {
      type: Object,
      required: true
    },
    channel: {
      type: Number,
      required: true
    },
    name: {
      type: String,
      default: ''
    },
    muted: {
      type: Boolean,
      default: true
    },
    controls: {
      type: Boolean,
      default: false
    },
    expanded: {
      type: Boolean,
      default: false
    },
    actions: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: true,
      failed: false,
      statusText: '',
      peerConnection: null,
      abortController: null,
      retryTimer: null,
      statusTimer: null,
      monitorTimer: null,
      destroyed: false
    }
  },
  computed: {
    label() {
      return this.name || `Cam. ${this.channel}`
    }
  },
  watch: {
    muted: 'syncMuted'
  },
  mounted() {
    this.syncMuted()
    this.start()
  },
  beforeDestroy() {
    this.destroyed = true
    this.teardown()
  },
  methods: {
    syncMuted() {
      if (this.$refs.video) {
        this.$refs.video.muted = this.muted
      }
    },
    async start() {
      this.loading = true
      this.failed = false
      this.statusText = this.$t('vehicleDetail.camera_waiting')
      try {
        await startStreaming(this.device, this.channel)
      } catch (error) {
        Vue.$log.debug('CameraTile start failed', this.channel, error)
        this.fail(this.$t('vehicleDetail.camera_command_failed'))
        return
      }
      this.waitForStream(Date.now() + STREAM_WAIT_MS)
    },
    // Poll the media server until the camera is actually publishing video,
    // rather than firing WebRTC negotiation at a path that has no stream
    // yet. Distinguishes "camera never connected" from "connected, no video".
    async waitForStream(deadline) {
      if (this.destroyed) {
        return
      }
      const status = await getMediaStatus(this.device.uniqueId, this.channel - 1)
      if (this.destroyed) {
        return
      }
      // Status endpoint unreachable / not deployed: don't block streaming
      // on it, just connect the way we always did.
      if (status === null) {
        this.statusText = ''
        this.connect()
        return
      }
      if (status.publishing && status.hasVideo) {
        this.statusText = ''
        this.connect()
        this.monitorStream()
        return
      }
      if (Date.now() >= deadline) {
        this.fail(status.connected
          ? this.$t('vehicleDetail.camera_no_video')
          : this.$t('vehicleDetail.camera_no_response'))
        return
      }
      this.statusText = status.connected
        ? this.$t('vehicleDetail.camera_waiting_video')
        : this.$t('vehicleDetail.camera_waiting')
      this.statusTimer = setTimeout(() => this.waitForStream(deadline), STATUS_POLL_MS)
    },
    // While streaming, keep an eye on the media server: if it drops the
    // publisher or stops getting frames, surface it instead of leaving a
    // frozen last frame with no explanation.
    async monitorStream() {
      if (this.destroyed || !this.peerConnection) {
        return
      }
      const status = await getMediaStatus(this.device.uniqueId, this.channel - 1)
      if (this.destroyed || !this.peerConnection) {
        return
      }
      if (status === null) {
        return // endpoint unavailable: leave WebRTC's own reconnect to it
      }
      if (!status.publishing) {
        this.fail(this.$t('vehicleDetail.camera_stopped'))
        return
      }
      const stalled = status.videoAgeMs != null && status.videoAgeMs > STALL_AFTER_MS
      this.statusText = stalled ? this.$t('vehicleDetail.camera_stalled') : ''
      this.monitorTimer = setTimeout(() => this.monitorStream(), MONITOR_POLL_MS)
    },
    retry() {
      this.teardown()
      this.start()
    },
    connect(attempt = 0) {
      this.teardown()
      this.abortController = new AbortController()
      const peerConnection = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      })
      this.peerConnection = peerConnection
      peerConnection.ontrack = ({ streams }) => {
        if (this.$refs.video && streams[0]) {
          this.$refs.video.srcObject = streams[0]
          this.syncMuted()
        }
      }
      peerConnection.onconnectionstatechange = () => {
        if (['disconnected', 'failed'].includes(peerConnection.connectionState)) {
          this.loading = true
          this.scheduleReconnect(peerConnection, attempt + 1)
        }
      }
      peerConnection.oniceconnectionstatechange = () => {
        if (['disconnected', 'failed'].includes(peerConnection.iceConnectionState)) {
          this.loading = true
          this.scheduleReconnect(peerConnection, attempt + 1)
        }
      }
      peerConnection.addTransceiver('video', { direction: 'recvonly' })
      peerConnection.addTransceiver('audio', { direction: 'recvonly' })
      this.negotiate(peerConnection, this.abortController.signal).catch(() => {
        if (attempt < MAX_RECONNECT_ATTEMPTS) {
          this.scheduleReconnect(peerConnection, attempt + 1)
        } else {
          this.fail()
        }
      })
    },
    scheduleReconnect(peerConnection, attempt = 0) {
      if (attempt > MAX_RECONNECT_ATTEMPTS) {
        this.fail()
        return
      }
      if (this.peerConnection === peerConnection && !this.retryTimer) {
        this.retryTimer = setTimeout(() => this.connect(attempt), 2000)
      }
    },
    async negotiate(peerConnection, signal) {
      const offer = await peerConnection.createOffer()
      await peerConnection.setLocalDescription(offer)
      await new Promise(resolve => {
        if (peerConnection.iceGatheringState === 'complete') {
          resolve()
          return
        }
        const timer = setTimeout(resolve, 5000)
        peerConnection.onicegatheringstatechange = () => {
          if (peerConnection.iceGatheringState === 'complete') {
            clearTimeout(timer)
            resolve()
          }
        }
      })
      const response = await fetch(getWhepUrl(this.device.uniqueId, this.channel - 1), {
        method: 'POST',
        headers: { 'Content-Type': 'application/sdp' },
        body: peerConnection.localDescription.sdp,
        signal
      })
      if (!response.ok) {
        throw new Error(`WHEP ${response.status}`)
      }
      await peerConnection.setRemoteDescription({
        type: 'answer',
        sdp: await response.text()
      })
    },
    fail(reason) {
      this.failed = true
      this.loading = false
      this.statusText = reason || ''
      this.teardown()
      this.$emit('failed', this.channel)
    },
    teardown() {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
      clearTimeout(this.statusTimer)
      this.statusTimer = null
      clearTimeout(this.monitorTimer)
      this.monitorTimer = null
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
      if (this.peerConnection) {
        this.peerConnection.close()
        this.peerConnection = null
      }
      if (this.$refs.video) {
        this.$refs.video.srcObject = null
      }
    }
  }
}
</script>

<style lang="scss">
  .cameraTile {
    position: relative;
    width: 100%;
    height: 100%;
    background: #000;
    overflow: hidden;

    video {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .cameraTile__label {
    position: absolute;
    top: 4px;
    left: 6px;
    color: #fff;
    font-size: 12px;
    text-shadow: 0 0 3px #000;
    pointer-events: none;
  }

  .cameraTile__actions {
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 0.15s;
  }

  .cameraTile:hover .cameraTile__actions {
    opacity: 1;
  }

  .cameraTile__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: rgba(0, 0, 0, 0.35);
    font-size: 28px;
    pointer-events: none;
  }

  .cameraTile__overlay--failed {
    background: rgba(0, 0, 0, 0.65);
    font-size: 13px;
    text-align: center;
    padding: 8px;

    .el-button {
      pointer-events: auto;
      font-size: 13px;
    }
  }

  .cameraTile__status {
    font-size: 12px;
    text-align: center;
    padding: 0 8px;
  }
</style>
